import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
// import { useAuth } from "../../contexts/AuthContext";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  Building2,
  Download,
  Edit2,
  Plus,
  Receipt,
  Search,
  X,
  AlertCircle,
} from "lucide-react";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import type { TourOperatorApi } from "../../services/advanceReceiptService";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { getApprovedTourOperators } from "../../services/advanceReceiptService";
import {
  getBookingReceipts,
  postBookingReceipt,
  downloadBookingReceiptPdf,
} from "../../services/bookingReceiptService";
import { FullScreenLoading } from "components/ui/loader";
import Pagination from "../../ui/pagination";

interface BookingReceiptDemo {
  id: string;
  receiptNo: string;
  bookingId: string;
  tourId: string;
  bookingLabel?: string;
  tourLabel?: string;
  tourOperatorId: number;
  toName: string;
  lawazimAmount: number;
  totalReceiptAmount: number;
  currency: string;
  receivedDate: string;
  receivedBy: string;
  createdBy: string;
  paymentMode: string;
  transactionRef: string;
  description: string;
  remarks: string;
  status: "DRAFT" | "POSTED";
}

const emptyFilters = {
  dateFrom: "",
  dateTo: "",
};

const columns = [
  "Receipt No",
  "Tour ID",
  "Tour Operator Name",
  "Receipt Amount ($)",
  "Received Date",
  "Received By",
  "Status",
  "Actions",
] as const;

type SortColumn = Exclude<(typeof columns)[number], "Actions">;

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const getOperatorName = (
  receipt: BookingReceiptDemo,
  tourOperators: TourOperatorApi[],
) => {
  const operator = tourOperators.find(
    (item) => item.tourOperatorId === receipt.tourOperatorId,
  );

  return operator?.officeName ?? receipt.toName ?? "—";
};

const getSortValue = (
  receipt: BookingReceiptDemo,
  column: SortColumn,
  tourOperators: TourOperatorApi[],
): string | number => {
  switch (column) {
    case "Receipt No":
      return receipt.receiptNo;

    case "Tour ID":
      return receipt.tourLabel || receipt.tourId;

    case "Tour Operator Name":
      return getOperatorName(receipt, tourOperators);

    case "Receipt Amount ($)":
      return receipt.totalReceiptAmount;

    case "Received Date":
      return receipt.receivedDate;

    case "Received By":
      return receipt.receivedBy;

    case "Status":
      return receipt.status === "DRAFT" ? "Draft" : "Paid";
  }
};

export function BookingReceiptListPage() {
  const navigate = useNavigate();
  // const { user } = useAuth();
  const [draftToPost, setDraftToPost] = useState<BookingReceiptDemo | null>(
    null,
  );
  const [receipts, setReceipts] = useState<BookingReceiptDemo[]>([]);
  const [tourOperators, setTourOperators] = useState<TourOperatorApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const { setPageHeader } = usePageHeader();

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [sortColumn, setSortColumn] = useState<SortColumn | "">("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const {
    canAdd,
    canEdit,
    canApprove,
    screenName,
    icon: ScreenIcon,
  } = useScreenPermission();

  const handleDownloadReceipt = async (receipt: BookingReceiptDemo) => {
    try {
      const pdfBlob = await downloadBookingReceiptPdf(receipt.id);

      const url = window.URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${receipt.receiptNo.replace(
        /[^a-zA-Z0-9_-]/g,
        "_",
      )}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      toast.success(`Receipt ${receipt.receiptNo} downloaded successfully`);
    } catch (error) {
      console.error("Failed to download booking receipt PDF:", error);

      toast.error("Failed to download booking receipt.");
    }
  };

  useEffect(() => {
    const loadBookingReceipts = async () => {
      try {
        setLoading(true);

        const [receiptResponse, operatorResponse] = await Promise.all([
          getBookingReceipts(
            pageNo,
            pageSize,
            search,
            filters.dateFrom,
            filters.dateTo,
          ),
          getApprovedTourOperators(),
        ]);

        const operators = operatorResponse;

        setTourOperators(operators);

        const mappedReceipts: BookingReceiptDemo[] =
          receiptResponse.data.items.map((item) => ({
            id: item.publicId,
            receiptNo: item.receiptNumber,
            bookingId: "",
            tourId: item.tourCode,
            tourLabel: item.tourCode,
            tourOperatorId: item.tourOperatorId,
            toName:
              operators.find(
                (operator) => operator.tourOperatorId === item.tourOperatorId,
              )?.officeName ?? `Operator ${item.tourOperatorId}`,
            lawazimAmount: 0,
            totalReceiptAmount: item.amountReceived,
            currency: "USD",
            receivedDate: item.receivedDate,
            receivedBy: item.receivedBy,
            createdBy: String(item.createdBy),
            paymentMode: item.paymentMode,
            transactionRef: item.transactionReference,
            description: item.description,
            remarks: item.remarks,
            status: item.status,
          }));

        setReceipts(mappedReceipts);
        setTotalCount(receiptResponse.data.totalCount);
      } catch (error) {
        console.error("Failed to load booking receipts:", error);

        setReceipts([]);
        toast.error("Failed to load booking receipts.");
      } finally {
        setLoading(false);
      }
    };

    loadBookingReceipts();
  }, [pageNo, pageSize, search, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });
    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  const invalidDateRange = !!(
    filters.dateFrom &&
    filters.dateTo &&
    filters.dateFrom > filters.dateTo
  );

  const filteredReceipts = receipts;

  const sortedReceipts = [...filteredReceipts].sort((a, b) => {
    if (!sortColumn) {
      return 0;
    }
    const left = getSortValue(a, sortColumn, tourOperators);
    const right = getSortValue(b, sortColumn, tourOperators);
    const comparison =
      typeof left === "number" && typeof right === "number"
        ? left - right
        : String(left).localeCompare(String(right), undefined, {
            numeric: true,
            sensitivity: "base",
          });
    return comparison * (sortDirection === "asc" ? 1 : -1);
  });

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6">
        <Card
          className="rounded-2xl border-sky-200 bg-white shadow-none"
          style={{ marginTop: 0, gap: 0 }}
        >
          <CardContent className="p-5 sm:p-6">
            <div className="flex flex-col gap-2 xl:flex-row xl:items-end xl:gap-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 xl:w-[410px] xl:shrink-0">
                {/* From date */}
                <div className="space-y-1.5">
                  <Label
                    htmlFor="receipt-from-date"
                    className="text-xs font-medium text-slate-600"
                  >
                    From
                  </Label>

                  <Input
                    id="receipt-from-date"
                    type="date"
                    className="relative h-11 pr-10 border-slate-300 bg-slate-50 text-[#17365D] shadow-none [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:m-0"
                    value={filters.dateFrom}
                    max={filters.dateTo || undefined}
                    onChange={(event) => {
                      setFilters({
                        ...filters,
                        dateFrom: event.target.value,
                      });
                      setPageNo(1);
                    }}
                  />
                </div>

                {/* To date */}
                <div className="space-y-1.5">
                  <Label
                    htmlFor="receipt-to-date"
                    className="text-xs font-medium text-slate-600"
                  >
                    To
                  </Label>

                  <Input
                    id="receipt-to-date"
                    type="date"
                    className="relative h-11 pr-10 border-slate-300 bg-slate-50 text-[#17365D] shadow-none [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:m-0"
                    value={filters.dateTo}
                    min={filters.dateFrom || undefined}
                    onChange={(event) => {
                      setFilters({
                        ...filters,
                        dateTo: event.target.value,
                      });
                      setPageNo(1);
                    }}
                  />
                </div>
              </div>

              {/* Search */}
              <div className="relative flex-1 min-w-0 xl:max-w-xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  aria-label="Search booking receipts"
                  placeholder="Search by Tour Code"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPageNo(1);
                  }}
                  className="h-11 pl-10 pr-10 bg-slate-50 shadow-none"
                />
                {search && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Clear search"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-400"
                    onClick={() => setSearch("")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Create */}
              <Button
                type="button"
                className="h-11 shrink-0 gap-2 rounded-lg bg-[#5B9BD5] px-6 text-sm font-semibold text-white shadow-none hover:bg-[#4A8CC7] xl:ml-auto"
                onClick={() => navigate("/booking-receipts/create")}
              >
                <Plus className="h-4 w-4" />
                Create Receipt
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Invalid date range */}
        {invalidDateRange && (
          <p role="alert" className="text-sm text-slate-600">
            The end date must be on or after the start date.
          </p>
        )}

        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{ gap: "0px", marginTop: "10px" }}
        >
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("Receipt No")}
                    >
                      <div className="flex items-center gap-1">
                        Receipt No
                        {sortColumn === "Receipt No" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("Tour ID")}
                    >
                      <div className="flex items-center gap-1">
                        Tour Code
                        {sortColumn === "Tour ID" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("Tour Operator Name")}
                    >
                      <div className="flex items-center gap-1">
                        Tour Operator
                        {sortColumn === "Tour Operator Name" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("Receipt Amount ($)")}
                    >
                      <div className="flex items-center gap-1">
                        Receipt Amount ($)
                        {sortColumn === "Receipt Amount ($)" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("Received Date")}
                    >
                      <div className="flex items-center gap-1">
                        Received Date
                        {sortColumn === "Received Date" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("Received By")}
                    >
                      <div className="flex items-center gap-1">
                        Received By
                        {sortColumn === "Received By" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("Status")}
                    >
                      <div className="flex items-center gap-1">Status</div>
                    </TableHead>

                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-left"
                    >
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {sortedReceipts.map((receipt) => {
                    const isDraft = receipt.status === "DRAFT";
                    return (
                      <TableRow key={receipt.id} className="hover:bg-sky-50/30">
                        {/* Receipt number */}
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <Receipt className="w-4 h-4 text-[#5B9BD5]" />
                            </div>

                            <div className="flex flex-col">
                              <span className="text-gray-900 text-sm">
                                {receipt.receiptNo}
                              </span>

                              <span className="text-xs text-gray-500">
                                {receipt.paymentMode}
                              </span>
                            </div>
                          </div>
                        </TableCell>

                        {/* Tour ID */}
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {receipt.tourLabel || receipt.tourId || "—"}
                          </span>
                        </TableCell>

                        {/* Operator */}
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {getOperatorName(receipt, tourOperators)}
                            </span>
                          </div>
                        </TableCell>

                        {/* Amount */}
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            ${" "}
                            {receipt.totalReceiptAmount.toLocaleString("en-US")}
                          </span>
                        </TableCell>

                        {/* Date */}
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {formatDate(receipt.receivedDate)}
                          </span>
                        </TableCell>

                        {/* Received By */}
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {receipt.receivedBy}
                          </span>
                        </TableCell>

                        {/* Status */}
                        <TableCell className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
                              isDraft
                                ? "border-slate-300 bg-slate-100 text-slate-600"
                                : "border-green-300 bg-green-100 text-green-700"
                            }`}
                          >
                            {isDraft ? "DRAFT" : "POSTED"}
                          </Badge>
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="px-4 py-3 text-right">
                          <div className="flex items-center justify-start gap-1">
                            {/* Post Receipt */}
                            {canApprove && receipt.status === "DRAFT" ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDraftToPost(receipt)}
                                className="text-blue-600 hover:bg-blue-50 h-7 w-7 p-0"
                                title="Post Receipt"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 fill-blue-100 text-blue-600" />
                              </Button>
                            ) : (
                              <div className="h-7 w-7" />
                            )}

                            {/* Download */}
                            {canAdd && receipt.status === "POSTED" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadReceipt(receipt)}
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                title="Download receipt"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            )}

                            {/* Edit */}
                            {canEdit && receipt.status === "DRAFT" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Edit receipt"
                                aria-label={`Edit receipt ${receipt.receiptNo}`}
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                onClick={() =>
                                  navigate(
                                    `/booking-receipts/edit/${encodeURIComponent(
                                      receipt.id,
                                    )}`,
                                  )
                                }
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {filteredReceipts.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="py-12 text-center text-sm text-gray-500"
                      >
                        No booking receipts found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {sortedReceipts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No booking receipts found</p>
                </div>
              ) : (
                sortedReceipts.map((receipt) => {
                  const isDraft = receipt.status === "DRAFT";

                  return (
                    <Card
                      key={receipt.id}
                      className="border-[#5B9BD5]/30 hover:shadow-md transition-shadow"
                    >
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                <Receipt className="w-5 h-5 text-[#5B9BD5]" />
                              </div>

                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-gray-900 truncate">
                                  {receipt.receiptNo}
                                </h3>

                                <p className="text-xs text-gray-500 mt-1">
                                  {formatDate(receipt.receivedDate)}
                                </p>
                              </div>
                            </div>

                            <Badge className="bg-[#5B9BD5] text-white shrink-0">
                              ${" "}
                              {receipt.totalReceiptAmount.toLocaleString(
                                "en-US",
                              )}
                            </Badge>
                          </div>

                          {/* Details */}
                          <div className="space-y-2 text-sm">
                            {/* Tour Code */}
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-600">Tour Code:</span>

                              <span className="font-medium text-gray-900 text-right">
                                {receipt.tourLabel || receipt.tourId || "—"}
                              </span>
                            </div>

                            {/* Tour Operator */}
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-600">
                                Tour Operator:
                              </span>

                              <span className="font-medium text-gray-900 flex items-center gap-1 text-right">
                                <Building2 className="w-3 h-3 flex-shrink-0" />
                                {getOperatorName(receipt, tourOperators)}
                              </span>
                            </div>

                            {/* Received Date */}
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-600">
                                Received Date:
                              </span>

                              <span className="font-medium text-gray-900">
                                {formatDate(receipt.receivedDate)}
                              </span>
                            </div>

                            {/* Received By */}
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-600">
                                Received By:
                              </span>

                              <span className="font-medium text-gray-900 text-right">
                                {receipt.receivedBy}
                              </span>
                            </div>

                            {/* Payment Mode */}
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-600">
                                Payment Mode:
                              </span>

                              <Badge
                                variant="outline"
                                className="text-xs border-[#5B9BD5]/30 text-[#5B9BD5]"
                              >
                                {receipt.paymentMode}
                              </Badge>
                            </div>

                            {/* Status */}
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-600">Status:</span>

                              <Badge
                                variant="outline"
                                className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
                                  isDraft
                                    ? "border-slate-300 bg-slate-100 text-slate-600"
                                    : "border-green-300 bg-green-100 text-green-700"
                                }`}
                              >
                                {isDraft ? "Draft" : "Paid"}
                              </Badge>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 pt-2 border-t border-gray-100">
                            {canApprove && isDraft && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDraftToPost(receipt)}
                                className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Post Receipt
                              </Button>
                            )}

                            {canAdd && !isDraft && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadReceipt(receipt)}
                                className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download
                              </Button>
                            )}

                            {canEdit && isDraft && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  navigate(
                                    `/booking-receipts/edit/${encodeURIComponent(
                                      receipt.id,
                                    )}`,
                                  )
                                }
                                className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50"
                              >
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="booking receipts"
          />
        </Card>

        {/* Save draft dialog */}
        <Dialog
          open={!!draftToPost}
          onOpenChange={(open) => !open && setDraftToPost(null)}
        >
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-600">
                <AlertCircle className="w-5 h-5" />
                Confirm change receipt to post.
              </DialogTitle>
              <DialogDescription className="text-gray-600 pt-2">
                Are you sure you want to post this draft? This action cannot be
                undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-4">
              <Button
                variant="outline"
                onClick={() => setDraftToPost(null)}
                className="border-gray-300"
              >
                Cancel
              </Button>
              <Button
                disabled={posting}
                onClick={async () => {
                  if (!draftToPost) {
                    return;
                  }

                  try {
                    setPosting(true);

                    const response = await postBookingReceipt(draftToPost.id);

                    if (!response.success) {
                      throw new Error(
                        response.message || "Failed to post receipt.",
                      );
                    }

                    setReceipts((previous) =>
                      previous.map((receipt) =>
                        receipt.id === draftToPost.id
                          ? {
                              ...receipt,
                              status: "POSTED",
                            }
                          : receipt,
                      ),
                    );

                    setDraftToPost(null);

                    toast.success(
                      response.message || "Receipt posted successfully.",
                    );
                  } catch (error) {
                    console.error("Failed to post booking receipt:", error);

                    const message =
                      error instanceof Error
                        ? error.message
                        : "Failed to post receipt.";

                    toast.error(message);
                  } finally {
                    setPosting(false);
                  }
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {posting ? "Posting..." : "Post receipt"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
